import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider } from "antd";
import { Navigate, useLocation, useNavigate, useParams, type NavigateOptions } from "react-router-dom";

import TraineeProfile from "~/modules/Trainee/TraineeProfile/TraineeProfile";
import { OpenTraineeModal } from "./OpenTraineeModal";
import { TraineeTabStrip } from "./TraineeTabStrip";
import { traineeWorkspace, useTraineeWorkspaceCards, type TraineeCard } from "./traineeWorkspaceStore";

/**
 * `/trainee/details/:id` - karty otwartych klientek. Każde przejście do profilu (z listy,
 * czatu, powiadomień) otwiera albo przełącza kartę, więc wejścia do profilu nie musiały się
 * zmieniać. URL należy do widocznej karty, karty w tle zostają zamontowane z własnymi danymi
 * i realtime, więc powrót do nich jest natychmiastowy.
 */
const TraineeWorkspace = () => {
  const { id } = useParams<{ id: string }>();
  const { search: locationSearch } = useLocation();
  const navigate = useNavigate();
  const storedCards = useTraineeWorkspaceCards();
  const [searchOpen, setSearchOpen] = useState(false);
  // Settery kart nie mogą zmieniać tożsamości przy każdej nawigacji, bo panele są memoizowane.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Wejście do otwartej już karty zwykłym linkiem (lista, czat) nie ma parametrów - wtedy karta wraca
  // do zapamiętanego widoku zamiast do domyślnej zakładki.
  const rememberedSearch = storedCards.find((card) => card.traineeId === id)?.search ?? "";
  const restoresSearch = !locationSearch && rememberedSearch.length > 1;
  const search = restoresSearch ? rememberedSearch : locationSearch;

  useEffect(() => {
    if (id && restoresSearch) {
      navigate(`/trainee/details/${id}${rememberedSearch}`, { replace: true });
    }
  }, [id, restoresSearch, rememberedSearch, navigate]);

  // Karta montuje się przy pierwszym otwarciu i zostaje zamontowana do zamknięcia.
  const [visited, setVisited] = useState<string[]>(() => (id ? [id] : []));

  useEffect(() => {
    if (id) {
      traineeWorkspace.open(id, search);
      setVisited((current) => (current.includes(id) ? current : [...current, id]));
    }
  }, [id, search]);

  // Przed pierwszym efektem aktywnej karty może jeszcze nie być w storze.
  const cards = useMemo<TraineeCard[]>(
    () =>
      !id || storedCards.some((card) => card.traineeId === id)
        ? storedCards
        : [...storedCards, { traineeId: id, search }],
    [storedCards, id, search],
  );

  const openTrainee = useCallback(
    (traineeId: string) => {
      const card = cards.find((item) => item.traineeId === traineeId);
      navigate(`/trainee/details/${traineeId}${card?.search ?? ""}`);
    },
    [cards, navigate],
  );

  const closeTrainee = useCallback(
    (traineeId: string) => {
      const index = cards.findIndex((card) => card.traineeId === traineeId);
      traineeWorkspace.close(traineeId);
      setVisited((current) => current.filter((visitedId) => visitedId !== traineeId));

      if (traineeId !== id) {
        return;
      }

      const neighbour = cards[index + 1] ?? cards[index - 1];
      navigate(neighbour ? `/trainee/details/${neighbour.traineeId}${neighbour.search}` : "/trainee");
    },
    [cards, id, navigate],
  );

  if (!id) {
    return <Navigate to="/trainee" replace />;
  }

  return (
    <div className="relative flex flex-col">
      <TraineeTabStrip
        traineeIds={cards.map((card) => card.traineeId)}
        activeId={id}
        onSelect={openTrainee}
        onClose={closeTrainee}
        onAdd={() => setSearchOpen(true)}
      />
      {cards
        .filter((card) => visited.includes(card.traineeId))
        .map((card) => (
          <div
            key={card.traineeId}
            role="tabpanel"
            aria-hidden={card.traineeId !== id}
            inert={card.traineeId !== id}
            // Bez display:none - karta w tle zachowuje swój rozmiar (przycięta do zera wysokości),
            // więc pokazanie jej nie budzi ResizeObserverów setek tooltipów (mapa aktywności).
            className={card.traineeId === id ? "" : "invisible absolute inset-x-0 top-0 h-0 overflow-hidden"}
          >
            <TraineeCardPane
              traineeId={card.traineeId}
              isActive={card.traineeId === id}
              // Widoczna karta dostaje parametry prosto z URL, karta w tle swój zapamiętany stan.
              search={card.traineeId === id ? search : card.search}
              navigateRef={navigateRef}
            />
          </div>
        ))}
      <OpenTraineeModal
        open={searchOpen}
        openTraineeIds={cards.map((card) => card.traineeId)}
        onClose={() => setSearchOpen(false)}
        onSelect={openTrainee}
      />
    </div>
  );
};

interface TraineeCardPaneProps {
  traineeId: string;
  isActive: boolean;
  search: string;
  navigateRef: React.MutableRefObject<ReturnType<typeof useNavigate>>;
}

// Memo: przełączenie karty zmienia propsy tylko dwóm kartom (poprzedniej i nowej), reszta się nie renderuje.
const TraineeCardPane = memo(({ traineeId, isActive, search, navigateRef }: TraineeCardPaneProps) => {
  const setSearch = useCallback(
    (nextSearch: string, options?: NavigateOptions) => {
      // Właściciela URL sprawdzamy w chwili zapisu - karta mogła przestać być widoczna od renderu.
      if (window.location.pathname === `/trainee/details/${traineeId}`) {
        navigateRef.current(`/trainee/details/${traineeId}${nextSearch}`, options);
        return;
      }
      traineeWorkspace.setSearch(traineeId, nextSearch);
    },
    [traineeId, navigateRef],
  );
  const navigate = useCallback(
    (to: string, options?: NavigateOptions) => navigateRef.current(to, options),
    [navigateRef],
  );

  // Modale, drawery i dropdowny karty renderują się w jej własnym kontenerze, a nie w `body`:
  // karta w tle (invisible + inert) chowa je razem ze sobą, więc otwarty modal klientki A nie zostaje
  // nad kartą B, np. po cofnięciu w przeglądarce.
  const portalRef = useRef<HTMLDivElement>(null);
  const getPopupContainer = useCallback(() => portalRef.current ?? document.body, []);

  return (
    <ConfigProvider getPopupContainer={getPopupContainer}>
      <TraineeProfile
        traineeId={traineeId}
        isActive={isActive}
        search={search}
        setSearch={setSearch}
        navigate={navigate}
      />
      <div ref={portalRef} />
    </ConfigProvider>
  );
});

export default TraineeWorkspace;
